/**
 * Altra campaign banner — dismissal + optional countdown.
 * Vanilla, no dependencies. Registered as a custom element so the Shopify theme
 * editor re-initialises it correctly when the section is reloaded.
 */
class AltraCampaignBanner extends HTMLElement {
  connectedCallback() {
    this.key = `altra-campaign-dismissed:${this.dataset.campaignId}`;

    if (this.dataset.dismissible === 'true' && this.#isDismissed()) {
      this.hidden = true;
      return;
    }

    this.querySelector('[data-campaign-dismiss]')?.addEventListener('click', () => this.#dismiss());

    if (this.dataset.deadline) this.#startCountdown(this.dataset.deadline);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  #isDismissed() {
    try {
      return localStorage.getItem(this.key) === '1';
    } catch {
      // Private mode / storage disabled — banner simply stays visible.
      return false;
    }
  }

  #dismiss() {
    try {
      localStorage.setItem(this.key, '1');
    } catch {
      /* non-fatal */
    }
    this.hidden = true;
    // Return focus to a sensible place so keyboard users are not stranded.
    document.querySelector('#MainContent, main')?.focus?.();
  }

  #startCountdown(deadline) {
    const target = new Date(deadline).getTime();
    if (Number.isNaN(target)) return;

    const out = this.querySelector('[data-campaign-countdown]');
    if (!out) return;

    const tick = () => {
      const diff = target - Date.now();

      if (diff <= 0) {
        clearInterval(this.timer);
        out.textContent = out.dataset.expiredText || '';
        return;
      }

      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);

      // Days-and-hours granularity: no ticking seconds, so nothing here churns
      // layout or spams assistive tech. The unit labels come from the section.
      out.textContent = d > 0 ? `${d}d ${h}h` : `${h}h ${m}m`;
    };

    tick();
    this.timer = setInterval(tick, 60000);
  }
}

if (!customElements.get('altra-campaign-banner')) {
  customElements.define('altra-campaign-banner', AltraCampaignBanner);
}
